import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  signal
} from '@angular/core';
import { linkedSignal } from '@solvace/zard/shared/utils/linked-signal-compat';

import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCheck } from '@ng-icons/lucide';

import {
  selectItemIconVariants,
  selectItemVariants,
  type ZardSelectItemAppearanceVariants,
  type ZardSelectItemMarkerVariants,
  type ZardSelectItemModeVariants,
  type ZardSelectSizeVariants,
} from '@solvace/zard/shared/components/select/select.variants';
import { mergeClasses, noopFn } from '@solvace/zard/shared/utils/merge-classes';
import { SOLVACE_ICONS } from '@solvace/zard/svz-icons/solvace-icons';

const DS_PICKED_ROW_CLASSES = 'text-primary hover:text-primary';

// Interface to avoid circular dependency
interface SelectHost {
  selectedValue(): string[];
  selectItem(value: string, label: string): void;
  navigateTo(): void;
}

@Component({
  standalone: true,
  selector: 'z-select-item, [z-select-item]',
  imports: [NgIcon],
  template: `
    @if (zCheckbox()) {
      <span
        class="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border"
        [class.border-primary]="isSelected()"
        [class.bg-primary]="isSelected()"
        [class.text-white]="isSelected()"
        [class.border-border]="!isSelected()"
        [class.bg-card]="!isSelected()"
      >
        @if (isSelected()) {
          <ng-icon name="lucideCheck" [strokeWidth]="3" class="icon-size-3" aria-hidden="true" data-testid="check-icon" />
        }
      </span>
    } @else if (isSelected()) {
      <span [class]="iconClasses()">
        @if (zMarker() === 'circle-check') {
          <ng-icon name="svcCheckC" class="z-select-item-circle-check" size="18px" aria-hidden="true" data-testid="check-icon" />
        } @else {
          <ng-icon name="lucideCheck" [strokeWidth]="strokeWidth()" aria-hidden="true" data-testid="check-icon" />
        }
      </span>
    }
    <!-- flex-1 + min-w-0 let projected row content (avatar, label, trailing marker) span the row; plain text still truncates. -->
    <span class="min-w-0 flex-1 truncate">
      <ng-content />
    </span>
  `,
  // The item variant forces `size-4` and `text-muted-foreground` on every svg without a size/text class (root config
  // `important: true`), so the design-system marker's svg takes its 18px and primary colour here, as `svz-chip` does.
  styles: [
    ':host ng-icon.z-select-item-circle-check ::ng-deep svg { width: 18px !important; height: 18px !important; color: inherit !important; }',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [provideIcons({ lucideCheck, svcCheckC: SOLVACE_ICONS.svcCheckC })],
  host: {
    role: 'option',
    tabindex: '-1',
    '[class]': 'classes()',
    '[class.text-primary]': 'isPickedInPrimary()',
    '[class.hover:text-primary]': 'isPickedInPrimary()',
    '[attr.value]': 'zValue()',
    '[attr.data-disabled]': 'zDisabled() ? "" : null',
    '[attr.data-selected]': 'isSelected() ? "" : null',
    '[attr.aria-selected]': 'isSelected()',
    '(click)': 'onClick()',
    '(mouseenter)': 'onMouseEnter()',
    '(keydown.{tab}.prevent)': 'noopFn',
  },
})
export class ZardSelectItemComponent {
  readonly elementRef = inject(ElementRef<HTMLElement>);

  readonly zValue = input.required<string>();
  readonly zDisabled = input(false, { transform: booleanAttribute });
  readonly zCheckbox = input(false, { transform: booleanAttribute });
  /** Additive. Selected-row marker: `check` (Zard default) or the design-system `circle-check` (18px, primary). */
  readonly zMarker = input<ZardSelectItemMarkerVariants>('check');
  /** Additive. Row look: `default` (Zard) or the design-system droplist row `ds` (`.sd-opt`). */
  readonly zAppearance = input<ZardSelectItemAppearanceVariants>('default');
  readonly class = input<string>('');

  private readonly select = signal<SelectHost | null>(null);
  noopFn = noopFn;

  readonly label = linkedSignal<string>(() => {
    const element = this.elementRef.nativeElement;
    return (element.textContent ?? element.innerText)?.trim() ?? '';
  });

  readonly zMode = signal<ZardSelectItemModeVariants>('normal');
  readonly zSize = signal<ZardSelectSizeVariants>('default');

  protected readonly classes = computed(() =>
    mergeClasses(
      selectItemVariants({ zMode: this.zMode(), zSize: this.zSize(), zMarker: this.zMarker(), zAppearance: this.zAppearance() }),
      this.class(),
      this.zAppearance() === 'ds' && this.isSelected() ? DS_PICKED_ROW_CLASSES : '',
    ),
  );

  protected readonly iconClasses = computed(() =>
    mergeClasses(selectItemIconVariants({ zMode: this.zMode(), zSize: this.zSize(), zMarker: this.zMarker() })),
  );

  protected readonly strokeWidth = computed(() => (this.zMode() === 'compact' ? 3 : 2));

  protected readonly isSelected = computed(() => this.select()?.selectedValue().includes(this.zValue()) ?? false);

  // `z-select` reuses `data-selected` as the keyboard/hover highlight, so the picked row's colour keys on the value.
  protected readonly isPickedInPrimary = computed(() => (this.zMarker() === 'circle-check' || this.zAppearance() === 'ds') && this.isSelected());

  setSelectHost(selectHost: SelectHost) {
    this.select.set(selectHost);
  }

  onMouseEnter() {
    if (this.zDisabled()) {
      return;
    }
    this.select()?.navigateTo();
  }

  onClick() {
    if (this.zDisabled()) {
      return;
    }
    this.select()?.selectItem(this.zValue(), this.label());
  }
}
